import { Detector } from 'react-detect-offline'
import { MedGEOLogoComponent } from 'assets/MedGEOlogo'
import { procedureStatusBarHeight } from 'components/ProcedureStatusBar'
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Toolbar,
  Typography,
  Tooltip,
  ListItem,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material'
import { Edit, ExpandLess, Logout, Menu } from '@mui/icons-material'
import styles from './floatingBar.module.css'
import {
  ANALYTICS,
  REFERRING_PHYSICIANS,
  SCRUB_TECHNICIANS,
  CATALOG,
} from 'app/ROUTES_CONSTANTS'
import { useFloatingBarLogic } from './FloatingBar.logic'
import { useUser } from 'app/User'

const isStaging = process.env.REACT_APP_ENV === 'staging'

function FloatingBar() {
  const logic = useFloatingBarLogic()
  const { isGuest, isRep } = useUser()

  const renderLogo = () => {
    return (
      <Button
        onClick={
          logic.isEditingSubmittedSurgery ? undefined : logic.handleLogoBtnClick
        }
        sx={{
          m: 0,
          p: 0,
          width: '120px',
          cursor:
            (isGuest && !isRep) || logic.isEditingSubmittedSurgery
              ? 'default'
              : 'pointer',
        }}
      >
        <Box display="flex">
          <MedGEOLogoComponent color="white" scale={0.6} />
        </Box>
      </Button>
    )
  }

  return (
    <AppBar
      id="medgeo-app-header"
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: logic.isEditingSubmittedSurgery
          ? 'grayscale.main'
          : isStaging
          ? 'darkgreen'
          : 'primary.main',
      }}
    >
      <Toolbar>
        <Box className={styles.toolbar}>
          <Box className={styles.heading} sx={{ width: 1, color: 'white' }}>
            {renderLogo()}
            <Typography sx={{ mx: 1, fontSize: '1.5rem', fontWeight: 300 }}>
              |
            </Typography>
            <Typography>{logic.dynamicTitle}</Typography>
          </Box>
          {logic.isEditingSubmittedSurgery && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
              }}
            >
              <Typography variant="h3">Addendum Mode</Typography>
            </Box>
          )}
          {logic.user && !logic.isEditingSubmittedSurgery && (
            <Box flexDirection={'row'} display="flex" alignItems="center">
              {logic.isDashboardActive && (
                <>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          color="secondary"
                          value={logic.darkMode}
                          onChange={logic.handleThemeMode}
                        />
                      }
                      label={
                        <Typography
                          variant="h5"
                          sx={{ color: 'white', textWrap: 'nowrap' }}
                        >
                          Dark Mode
                        </Typography>
                      }
                    />
                  </FormGroup>
                  <Typography
                    sx={{
                      mx: 1,
                      fontSize: '1.5rem',
                      fontWeight: 300,
                      color: 'white',
                    }}
                  >
                    |
                  </Typography>
                </>
              )}
              <Tooltip title="User Menu" placement="left">
                <IconButton
                  data-testid="menu-button"
                  sx={{ color: 'primary.lightest' }}
                  onClick={logic.handleMenuToggle}
                >
                  <Menu />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {logic.isEditingSubmittedSurgery && (
            <Button
              onClick={logic.handleExitAddendumMode}
              sx={{
                color: 'white',
                border: '1px solid white',
                '&:hover': {
                  border: '1px solid white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Exit
            </Button>
          )}
        </Box>
      </Toolbar>

      {/* Menu */}
      {!logic.isEditingSubmittedSurgery && (
        <Drawer
          open={logic.isMenuOpen}
          onClose={logic.handleMenuToggle}
          variant="temporary"
          anchor="top"
          sx={{ zIndex: 1000 }}
        >
          <List>
            <ListItem
              sx={{
                bgcolor: logic.isEditingSubmittedSurgery
                  ? 'grayscale.main'
                  : isStaging
                  ? 'darkgreen'
                  : 'primary.main',
                color: 'white',
                justifyContent: 'space-between',
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'white', color: 'primary.main' }} />
              </ListItemAvatar>
              <ListItemText>
                {`${logic.userRoleString?.toUpperCase()}`}
                {(logic.user?.firstName || logic.user?.lastName) &&
                  ` | ${logic.user?.firstName ?? ''} ${
                    logic.user?.lastName ?? ''
                  }`}
              </ListItemText>
              <IconButton
                onClick={logic.handleMenuToggle}
                sx={{ color: 'white' }}
              >
                <ExpandLess />
              </IconButton>
            </ListItem>

            {/* USER & ACTIONS */}
            {logic.user?.id && !logic.isGuest && (
              <ListItemButton
                data-testid="profile-button"
                onClick={() => logic.handleNavigate('profile')}
                sx={{ pt: 2 }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ backgroundColor: 'grayscale.light' }}>
                    <Edit
                      sx={{
                        color: 'primary.main',
                        backgroundColor: 'grayscale.light',
                      }}
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText>Profile</ListItemText>
              </ListItemButton>
            )}
            <ListItemButton
              data-testid="logout-button"
              divider={!logic.isGuest}
              onClick={logic.handleLogout}
            >
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: 'grayscale.light' }}>
                  <Logout
                    sx={{
                      color: 'primary.main',
                      backgroundColor: 'grayscale.light',
                    }}
                  />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary="Logout" />
            </ListItemButton>

            {/* APP NAVIGATION */}
            {!logic.isSPD && !logic.isGuest && (
              <ListItemButton
                data-testid="surgery-list"
                onClick={() => logic.handleNavigate('surgeries')}
              >
                <ListItemText>Vendor Case Log</ListItemText>
              </ListItemButton>
            )}
            {logic.flags.navigationMenuTrayManagement && !logic.isGuest && (
              <ListItemButton
                data-testid="surgery-list"
                onClick={() => logic.handleNavigate('tray-management')}
              >
                <ListItemText>Vendor Distribution Center</ListItemText>
              </ListItemButton>
            )}
            {logic.isNurseAdmin && (
              <ListItemButton onClick={() => logic.handleNavigate(ANALYTICS)}>
                <ListItemText>Analytics</ListItemText>
              </ListItemButton>
            )}
            {logic.isNurseAdmin && logic.flags.catalogPluginEnabled && (
              <ListItemButton onClick={() => logic.handleNavigate(CATALOG)}>
                <ListItemText>Catalog</ListItemText>
              </ListItemButton>
            )}
            {logic.flags.navigationMenuInventory && logic.isNurseAdmin && (
              <ListItemButton
                onClick={() => logic.handleNavigate('inventory-report')}
              >
                <ListItemText>Inventory</ListItemText>
              </ListItemButton>
            )}
            {logic.flags.navigationMenuReceiveProducts &&
              logic.isNurseAdmin && (
                <ListItemButton onClick={() => logic.handleNavigate('receive')}>
                  <ListItemText>Receive Products</ListItemText>
                </ListItemButton>
              )}
            <ListItemButton>
              <ListItemText>
                Send PO for pre-surgical authorization
              </ListItemText>
            </ListItemButton>
            <ListItemButton>
              <ListItemText>Send PO for post-surgical billing</ListItemText>
            </ListItemButton>
            {logic.isNurseAdmin && (
              <ListItemButton
                onClick={() => logic.handleNavigate(REFERRING_PHYSICIANS)}
              >
                <ListItemText>{logic.PhysiciansTitle}</ListItemText>
              </ListItemButton>
            )}
            <ListItemButton
              onClick={() => logic.handleNavigate(SCRUB_TECHNICIANS)}
            >
              <ListItemText>Reps</ListItemText>
            </ListItemButton>
          </List>
        </Drawer>
      )}

      {/* Offline Indicator */}
      {logic.user && (
        <Detector
          render={({ online }: { online: boolean }) => (
            <Collapse in={!online} className={styles.offlineIndicator}>
              <Box
                sx={{
                  backgroundColor: 'error.main',
                  height: procedureStatusBarHeight + 1,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    my: 0,
                    mx: 2,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                  }}
                >
                  You are offline
                </Typography>
              </Box>
            </Collapse>
          )}
        />
      )}
    </AppBar>
  )
}

export default FloatingBar
